import { Component, Inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EncounterService } from '../../_services/encounter.service';
import { AuthService } from '../../_services/auth.service';
import { DmyDateFieldComponent } from '../../shared/components/dmy-date-field/dmy-date-field.component';
import { dateToUtcMidnightIso, dateOnlyStringToLocalDate, normalizeSubjectDobToDateOnly } from '../../_helpers/dmy-date';
import { RegenerateAfterEditComponent, RegenerateAfterEditResult } from '../../shared/dialogs/regenerate-after-edit/regenerate-after-edit.component';
import { firstValueFrom } from 'rxjs';
import { APP_DIALOG_DATA } from '../../shared/components/dialog/dialog.tokens';
import { AppDialogRef } from '../../shared/components/dialog/dialog-ref';
import { DialogService } from '../../shared/components/dialog/dialog.service';

@Component({
  selector: 'app-manual-patient-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatButtonModule, MatFormFieldModule, MatInputModule, TranslateModule, DmyDateFieldComponent],
  templateUrl: './manual-patient-selector.component.html'
})
export class ManualPatientSelectorComponent {
  patientName = '';
  patientId = '';
  dateOfBirth: Date | null = null;
  buttonLabel: string;
  isLegalOrg = false;
  isConfirming = false;

  @ViewChild(DmyDateFieldComponent) dobField?: DmyDateFieldComponent;

  constructor(
    @Inject(APP_DIALOG_DATA) public data: {
      from: 'consultation' | 'edit';
      patientName?: string;
      patientId?: string;
      dateOfBirth?: Date | string | null;
      mode?: 'consultation' | 'dictation';
      dictationId?: string;
      flow?: 'encounter' | 'document';
      promptRegenerateAfterEdit?: boolean;
    },
    private dialogRef: AppDialogRef<ManualPatientSelectorComponent>,
    private dialog: DialogService,
    private encounterService: EncounterService,
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private translate: TranslateService
  ) {
    const user = this.authService.getUser();
    this.isLegalOrg = user?.entity?.settings?.category === 'Legal';
    if (data) {
      this.patientName = data.patientName ?? '';
      this.patientId = data.patientId ?? '';
      if (data.dateOfBirth) {
        this.dateOfBirth = this.parseIncomingDob(data.dateOfBirth);
      }
    }
    this.buttonLabel = data.from === 'edit'
      ? 'Save changes'
      : data.mode === 'dictation'
        ? 'Start dictation'
        : 'Start consultation';
  }

  private parseIncomingDob(raw: Date | string): Date | null {
    if (raw instanceof Date) {
      return isNaN(raw.getTime()) ? null : raw;
    }
    const norm = normalizeSubjectDobToDateOnly(raw);
    if (norm) {
      return dateOnlyStringToLocalDate(norm);
    }
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }

  goBack(): void {
    this.dialogRef.close('back');
  }

  close(): void {
    this.dialogRef.close('close');
  }

  async confirm(): Promise<{ saved: boolean, patient?: any } | undefined | void> {
    if (this.isConfirming) return;

    let dob: Date | null = null;
    if (!this.isLegalOrg) {
      if (!this.dobField || !this.dobField.validate()) {
        return;
      }
      dob = this.dobField.getDateIfValid();
    }
    const patient = {
      subject_name: this.patientName?.trim() || '',
      subject_id: this.patientId?.trim() || '',
      subject_dob: dob ? dateToUtcMidnightIso(dob) : ''
    };

    if (this.data.from === 'edit' && this.data.dictationId && !navigator.onLine) {
      this.snackBar.open(
        this.translate.instant('You are offline. Please check your internet connection and try again.'),
        this.translate.instant('Close'),
        {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'bottom'
        }
      );
      return;
    }

    const regenerateAfterEdit = await this.confirmDocumentRegenerateIfNeeded();
    if (this.data.flow === 'document'
      && this.data.promptRegenerateAfterEdit !== false
      && (regenerateAfterEdit === 'close' || regenerateAfterEdit === undefined)) {
        this.dialogRef.dismiss(regenerateAfterEdit);
        return;
    }

    this.isConfirming = true;
    try {
      if (this.data.from === 'edit') {
        // When in edit mode, update the patient and close the dialog without navigating
        if (this.data.dictationId) {
          const result = await this.encounterService.updatePatient(this.data.dictationId, patient, {
            manual: true,
            visit: null,
          });
          this.dialogRef.close(regenerateAfterEdit ? { ...result, regenerateAfterEdit } : result);
        } else {
          // If dictationId is missing in edit mode, close dialog without updating
          this.dialogRef.close(regenerateAfterEdit ? { saved: false, regenerateAfterEdit } : { saved: false });
        }
      } else {
        await this.encounterService.createAndNavigate(patient || null, this.data.mode || 'consultation', {
          manual: true,
          entryPoint: 'manual_patient',
        });
        this.dialogRef.close('close');
      }
    } finally {
      this.isConfirming = false;
    }
  }

  private async confirmDocumentRegenerateIfNeeded(): Promise<RegenerateAfterEditResult | undefined> {
    if (this.data.flow !== 'document' || this.data.promptRegenerateAfterEdit === false) {
      return undefined;
    }

    const regenerateDialogRef = this.dialog.open(RegenerateAfterEditComponent, {size: 'md'});
    return firstValueFrom(regenerateDialogRef.afterClosed());
  }
}
